import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslationService } from '../../core/services/translation.service';
import { UserStorageService } from '../../core/services/user-storage.service';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  actionLabel: string;
  actionRoute?: string;
  enabled: boolean;
}

/**
 * Generic first-run onboarding. This baseline ships a single "invite your
 * team" step — a concrete app built on this platform should replace/extend
 * `steps()` with its own entity-creation steps: one step per key entity,
 * `completed` driven by a real count query, `enabled` gating steps that
 * depend on an earlier one.
 */
@Component({
  selector: 'app-onboarding',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './onboarding.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './onboarding.component.css',
})
export class OnboardingComponent implements OnInit {
  private router = inject(Router);
  private translate = inject(TranslationService);
  private userStorage = inject(UserStorageService);

  organizationName = signal<string>('');
  steps = signal<OnboardingStep[]>([
    {
      id: 'invite-team',
      title: this.translate.translate('onboarding.steps.inviteTeam.title'),
      description: this.translate.translate('onboarding.steps.inviteTeam.description'),
      completed: false,
      actionLabel: this.translate.translate('onboarding.steps.inviteTeam.action'),
      actionRoute: '/settings/members',
      enabled: true,
    },
  ]);

  ngOnInit(): void {
    // TODO: Add API endpoint to get customer details and show the real org name
    this.organizationName.set(this.translate.translate('onboarding.organizationPlaceholder'));
  }

  handleAction(step: OnboardingStep): void {
    if (!step.enabled || !step.actionRoute) return;
    this.router.navigate([step.actionRoute]);
  }

  skipOnboarding(): void {
    // Mark onboarding as skipped and go to home
    this.userStorage.setTypedItem('onboarding_dismissed', true);
    this.router.navigate(['/']);
  }
}
